import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

import { AutoAssignPanel } from "./settings/AutoAssignPanel";
import { AssignmentRulesPanel } from "./settings/AssignmentRulesPanel";
import { TeamPanel } from "./settings/TeamPanel";
import { MSGraphPanel } from "./settings/MSGraphPanel";
import { MailboxesPanel } from "./settings/MailboxesPanel";
import { SystemAddressesPanel } from "./settings/SystemAddressesPanel";
import { EmploymentHeroPanel } from "./settings/EmploymentHeroPanel";
import { TaxonomyPanel } from "./settings/TaxonomyPanel";
import { RegionsPanel } from "./settings/RegionsPanel";
import { ITLocationsPanel } from "./settings/ITLocationsPanel";
import { ITHoursBankPanel } from "./settings/ITHoursBankPanel";
import { PortalTeamsPanel } from "./settings/PortalTeamsPanel";
import { BusinessHoursPanel } from "./settings/BusinessHoursPanel";
import { WelcomeEmailPanel } from "./settings/WelcomeEmailPanel";
import { PurgePanel } from "./settings/PurgePanel";

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [ssoStatus, setSsoStatus] = useState({ sso_enabled: false });
  const [settings, setSettings] = useState({
    auto_assign_mode: "off", agent_pool: [], sla_enabled: true,
    business_hours_enabled: true, business_hours_start: "08:00", business_hours_end: "17:00",
    business_days: [0, 1, 2, 3, 4], business_timezone: "Europe/London",
    email_ingest_enabled: true, email_send_enabled: true, email_auto_ack: true, email_reply_default: true,
    email_auto_ack_subject: "", email_auto_ack_body: "",
    email_notify_assignee: true,
    email_ingest_since: null,
    systems: [], issue_types: [],
    regions: [],
    ms_mailboxes: [],
    system_addresses: [],
    it_locations: [],
    assignment_rules: [],
  });
  const [saving, setSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [msStatus, setMsStatus] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/settings").then((r) => { setSettings(r.data); setSavedSnapshot(JSON.stringify(r.data)); });
    api.get("/inbox/ms/status").then((r) => setMsStatus(r.data)).catch(() => {});
    api.get("/auth/sso-status").then((r) => setSsoStatus(r.data)).catch(() => {});
  }, []);

  const isDirty = savedSnapshot !== null && JSON.stringify(settings) !== savedSnapshot;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings", settings);
      setSavedSnapshot(JSON.stringify(r.data));
      window.dispatchEvent(new Event("hd:settings-changed"));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const discard = () => {
    if (savedSnapshot) setSettings(JSON.parse(savedSnapshot));
  };

  const renameTaxonomy = async (listKey, from, to) => {
    try {
      const r = await api.post("/settings/taxonomy/rename", {
        list_key: listKey, from_value: from, to_value: to,
      });
      setSettings((s) => {
        const next = { ...s, [listKey]: r.data.values };
        setSavedSnapshot(JSON.stringify(next));
        return next;
      });
      const suffix = r.data.tickets_updated
        ? ` · ${r.data.tickets_updated} ticket${r.data.tickets_updated === 1 ? "" : "s"} updated`
        : "";
      toast.success(`Renamed "${from}" → "${to}"${suffix}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
      throw e;
    }
  };

  const renameRegion = async (from, to) => {
    try {
      const r = await api.post("/settings/taxonomy/rename", {
        list_key: "regions", from_value: from, to_value: to,
      });
      setSettings((s) => {
        const next = { ...s, regions: r.data.values };
        setSavedSnapshot(JSON.stringify(next));
        return next;
      });
      const suffix = r.data.tickets_updated
        ? ` · ${r.data.tickets_updated} ticket${r.data.tickets_updated === 1 ? "" : "s"} updated`
        : "";
      toast.success(`Renamed region "${from}" → "${to}"${suffix}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
      throw e;
    }
  };

  const renameLocation = async (regionName, from, to) => {
    try {
      const r = await api.post("/settings/taxonomy/rename", {
        list_key: "regions", region: regionName, from_value: from, to_value: to,
      });
      setSettings((s) => {
        const next = { ...s, regions: r.data.values };
        setSavedSnapshot(JSON.stringify(next));
        return next;
      });
      const suffix = r.data.tickets_updated
        ? ` · ${r.data.tickets_updated} ticket${r.data.tickets_updated === 1 ? "" : "s"} updated`
        : "";
      toast.success(`Renamed location "${from}" → "${to}"${suffix}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
      throw e;
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Sticky "unsaved changes" bar */}
      {isAdmin && isDirty && (
        <div
          data-testid="settings-dirty-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 hairline rounded-lg bg-[#4f4c90] shadow-2xl px-4 py-2.5 flex items-center gap-3 border border-slate-300"
        >
          <div className="text-sm text-white flex items-center gap-2">
            <Warning size={14} weight="fill" className="text-yellow-300" />
            You have unsaved changes
          </div>
          <Button
            variant="ghost"
            className="text-slate-800 hover:text-slate-900 h-8 px-3"
            onClick={discard}
            disabled={saving}
            data-testid="settings-discard"
          >Discard</Button>
          <Button
            className="bg-[#39A36E] hover:bg-[#2E8C5D] h-8 px-4 gap-1"
            onClick={save}
            disabled={saving}
            data-testid="settings-save-sticky"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}

      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure your Business Systems Support workspace.</p>
      </div>

      <div className="hairline rounded-lg p-5 bg-white">
        <div className="font-heading font-semibold">Profile</div>
        <div className="flex items-center gap-3 mt-3">
          <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
            {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : initials(user?.name)}
          </div>
          <div>
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-xs text-slate-500 font-mono">{user?.email}</div>
            <div className="chip !text-slate-700 !border-slate-300 bg-slate-100 mt-1 capitalize">{user?.role}</div>
          </div>
        </div>
      </div>

      <AutoAssignPanel
        settings={settings}
        setSettings={setSettings}
        users={users}
        isAdmin={isAdmin}
        saving={saving}
        onSave={save}
      />

      <AssignmentRulesPanel
        settings={settings}
        setSettings={setSettings}
        users={users}
        isAdmin={isAdmin}
      />

      <TeamPanel users={users} setUsers={setUsers} currentUser={user} isAdmin={isAdmin} ssoEnabled={ssoStatus.sso_enabled} />

      <MSGraphPanel
        settings={settings}
        setSettings={setSettings}
        msStatus={msStatus}
        setMsStatus={setMsStatus}
        isAdmin={isAdmin}
      />

      <MailboxesPanel
        settings={settings}
        setSettings={setSettings}
        isAdmin={isAdmin}
      />

      <SystemAddressesPanel
        settings={settings}
        setSettings={setSettings}
        isAdmin={isAdmin}
      />

      <EmploymentHeroPanel isAdmin={isAdmin} />

      <BusinessHoursPanel settings={settings} setSettings={setSettings} isAdmin={isAdmin} />

      <TaxonomyPanel
        settings={settings}
        setSettings={setSettings}
        isAdmin={isAdmin}
        onRename={renameTaxonomy}
      />

      <RegionsPanel
        settings={settings}
        setSettings={setSettings}
        isAdmin={isAdmin}
        onRenameRegion={renameRegion}
        onRenameLocation={renameLocation}
      />

      <ITLocationsPanel settings={settings} setSettings={setSettings} isAdmin={isAdmin} />

      <ITHoursBankPanel settings={settings} isAdmin={isAdmin} />

      <WelcomeEmailPanel settings={settings} setSettings={setSettings} isAdmin={isAdmin} />

      <PortalTeamsPanel isAdmin={isAdmin} />

      {isAdmin && <PurgePanel />}
    </div>
  );
}
